'use client';

import type { SharedButtonProps } from './Button2';

/**
 * Disables interaction props if `isLoading` is true.
 *
 * This function takes a props object that may contain interaction handlers and returns a new object
 * with those handlers omitted if `isLoading` is true. This prevents interaction while the button is loading.
 *
 * @template T - The type of the props object, extending ButtonProps and optionally including `isLoading`.
 * @param {T} props - The props object that may contain interaction handlers.
 * @returns {Partial<T>} - The props object with interaction handlers omitted if `isLoading` is true.
 *
 * @example
 * const buttonProps = {
 *   isLoading: true,
 *   onPress: () => console.log('Pressed'),
 *   onKeyDown: () => console.log('Key down')
 * };
 * const disabledProps = disablePendingProps(buttonProps);
 * // disabledProps will not contain onPress and onKeyDown
 * // Similar to Spectrum's pattern here:
 * // https://github.com/adobe/react-spectrum/blob/9e79420c19ebb36ed8b52e407515f5cd55aaffae/packages/%40react-spectrum/button/src/Button.tsx#L36
 */
export function disablePendingProps<
  T extends SharedButtonProps & { isLoading?: boolean }
>(props: T): Partial<T> {
  // don't allow interaction while isPending is true
  if (props.isLoading) {
    const {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      onPress,
      onPressStart,
      onPressEnd,
      onPressChange,
      onPressUp,
      onKeyDown,
      onKeyUp,
      href,
      /* eslint-enable @typescript-eslint/no-unused-vars */
      ...rest
    } = props;
    return rest as Partial<T>;
  }
  return props;
}
